import { useEffect, useState } from "react";
import { DateTimeDisplay, StatusMessage, Breadcrumbs } from "../../components/ui";
import MapView from "../../components/maps/Map";
import Modal from "../../components/ui/Modal";
import { ModalContent } from "../../components/ui/ModalContent";
import { TOAST_ERROR_MS } from "../../constants/timeouts";
import { useBreadcrumbs } from "../../hooks/useBreadcrumbs";
import { useAuth0 } from "../../contexts/useAuthContext";
import { BuildingEditor } from "../../features/buildings/BuildingEditor";
import { BuildingList } from "../../features/buildings/BuildingList";
import { useBuildings } from "../../features/buildings/useBuildings";
import type { ConfirmBuildingAction, CustomerWithSite } from "../../features/buildings/buildingRules";
import type { CreateCustomerRequest } from "../../services/customer/customer.types";
import type { RuleFormData } from "../../components/forms/BuildingForm";

interface Confirmation {
  heading: string;
  message: string;
  action: () => Promise<void>;
}

const Buildings = () => {
  const data = useBuildings();
  const [editor, setEditor] = useState<{ customer: CustomerWithSite | null } | null>(null);
  const [confirmation, setConfirmation] = useState<Confirmation | null>(null);
  const breadcrumbs = useBreadcrumbs({ currentPageTitle: "Buildings" });
  const { isAdmin } = useAuth0();
  const confirm: ConfirmBuildingAction = (heading, message, action) => setConfirmation({ heading, message, action });

  useEffect(() => {
    const deletedCustomer = data.success?.startsWith("Successfully deleted ");
    const deletedSite = data.success?.startsWith('Site "');
    if (!deletedCustomer && !deletedSite) {return;}
    const timeout = setTimeout(() => data.setSuccess(null), deletedCustomer ? TOAST_ERROR_MS : 4000);
    return () => clearTimeout(timeout);
  }, [data.success, data.setSuccess]);

  const openEditor = async (customer: CustomerWithSite | null) => {
    data.setError(null);
    data.setSuccess(null);
    if (customer && !data.details[customer.id]) {
      const refreshed = await data.refreshCustomer(customer);
      if (refreshed) {setEditor({ customer: refreshed });}
      return;
    }
    setEditor({ customer });
  };

  const save = async (values: CreateCustomerRequest, rules: RuleFormData[] | null) => {
    const saved = await data.save(values, rules, editor?.customer ?? null);
    if (saved) {setEditor(null);}
    return saved;
  };

  const remove = (customer: CustomerWithSite) => {
    confirm("Delete Customer", "Are you sure you want to delete this customer?", () => data.remove(customer));
  };

  const refreshEditor = async () => {
    if (!editor?.customer) {return;}
    const updated = await data.refreshCustomer(editor.customer);
    if (updated) {setEditor({ customer: updated });}
  };

  return (
    <main>
      <div className="container mb-2">
        {editor && (
          <BuildingEditor
            customer={editor.customer}
            submitting={data.submitting}
            error={data.error}
            success={data.success}
            onError={data.setError}
            onSuccess={data.setSuccess}
            confirm={confirm}
            onSave={save}
            onCancel={() => setEditor(null)}
            onChanged={refreshEditor}
            onSiteDeleted={name => data.setSuccess(`Site "${name}" deleted successfully`)}
          />
        )}
        <Modal isOpen={confirmation !== null} title="Confirm Changes"
          headerClass={confirmation?.heading.toLowerCase().startsWith("delete") ? "modal__header--danger" : undefined}
          content={<ModalContent heading={confirmation?.heading} message={confirmation?.message} />}
          onConfirm={async () => {
            const action = confirmation?.action;
            setConfirmation(null);
            if (action) {await action();}
          }}
          onCancel={() => setConfirmation(null)}
        />
        <section className="page">
          <div className="heading__section">
            <div className="heading__title-group">
              <h2 className="heading__title heading__title--building">Buildings</h2>
              <DateTimeDisplay />
              <Breadcrumbs items={breadcrumbs} />
              
            </div>
            {!editor && (
              <div><button type="button" className="btn btn-add-new" onClick={() => openEditor(null)}>Add New Customer</button></div>
            )}
          </div>
          {!editor && data.success && <StatusMessage kind="success" onClose={() => data.setSuccess(null)}>{data.success}</StatusMessage>}
        </section>
        {!editor && data.error && <StatusMessage kind="error" onClose={() => data.setError(null)}>{data.error}</StatusMessage>}
        <section className="page">
          <div className="alarm-content">
            <BuildingList customers={data.customers} details={data.details} detailsLoading={data.detailsLoading}
              loading={data.loading} editing={Boolean(editor?.customer)} isAdmin={isAdmin()}
              onEdit={openEditor} onDelete={remove} />
          </div>
          {data.submitting && <p>Saving customer…</p>}
        </section>
        <section className="mt-2"><MapView refreshKey={data.mapRefreshKey} /></section>
      </div>
    </main>
  );
};

export default Buildings;
